import {IAppState, IFeatureToggles, initialFeatureToggles} from "./Helper";
import {StorageConstants} from "../../util/constants";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {SliceNames} from "../constants";
import {getFeatureToggles, getServerTime} from "./ActionCreators";

const initialValue: IAppState = {
    language: localStorage.getItem(StorageConstants.LANGUAGE) ?? "mk",
    serverTime: null,
    error: false,
    errorCode: undefined,
    featureToggles: initialFeatureToggles,
    spinTheWheelOpen: false,
    promoModalOpen: false,
    appLoading: true
}

const normalizeCanceledOrderPoints = (rawValue: any) => {
    if (!rawValue || typeof rawValue !== "object") {
        return initialFeatureToggles.canceled_order_points
    }

    const enabled = typeof rawValue.enabled === "boolean"
        ? rawValue.enabled
        : (typeof rawValue.value === "boolean" ? rawValue.value : false)

    const numericValue = typeof rawValue.value === "number"
        ? rawValue.value
        : Number(rawValue.points ?? initialFeatureToggles.canceled_order_points.value)
    const numericCooldownDays = typeof rawValue.email_cooldown_days === "number"
        ? rawValue.email_cooldown_days
        : Number(
            rawValue.emailCooldownDays
            ?? initialFeatureToggles.canceled_order_points.email_cooldown_days
        )

    return {
        enabled,
        value: Number.isFinite(numericValue) ? Math.max(Math.floor(numericValue), 0) : 0,
        email_cooldown_days: Number.isFinite(numericCooldownDays)
            ? Math.max(Math.floor(numericCooldownDays), 0)
            : initialFeatureToggles.canceled_order_points.email_cooldown_days,
    }
}

const appSlice = createSlice({
    name: SliceNames.APP,
    initialState: initialValue,
    reducers: {
        changeLanguage: (state, action: PayloadAction<string>) => {
            localStorage.setItem(StorageConstants.LANGUAGE, action.payload)
            state.language = action.payload
        },
        setApplicationError: (state, action: PayloadAction<{ isError: boolean, statusCode: number | undefined }>) => {
            state.error = action.payload.isError
            state.errorCode = action.payload.statusCode
        },
        updateServerTime: (state) => {
            state.serverTime = state.serverTime !== null ? state.serverTime + 1000 : null;
        },
        updateRequiredLogin: (state, action: PayloadAction<boolean>) => {
            state.featureToggles = {
                ...state.featureToggles,
                login_required: action.payload,
            }
        },
        toggleSpinTheWheel: (state, action: PayloadAction<boolean>) => {
            state.spinTheWheelOpen = action.payload
        },
        togglePromoModal: (state, action: PayloadAction<boolean>) => {
            state.promoModalOpen = action.payload
        }
    },
    extraReducers: builder => {
        builder.addCase(
            getServerTime.fulfilled, (state, action: PayloadAction<number>) => {
                state.serverTime = action.payload
            })
            .addCase(
                getFeatureToggles.pending, (state) => {
                    state.appLoading = true
                })
            .addCase(
                getFeatureToggles.fulfilled, (state, action: PayloadAction<IFeatureToggles>) => {
                    state.featureToggles = {
                        ...initialFeatureToggles,
                        ...action.payload,
                        canceled_order_points: normalizeCanceledOrderPoints(
                            (action.payload as any)?.canceled_order_points
                        ),
                    }
                    state.appLoading = false
                })
            .addCase(
                getFeatureToggles.rejected, (state) => {
                    state.featureToggles = initialFeatureToggles
                    state.appLoading = false
                })
    }
})

export default appSlice.reducer

export const {
    changeLanguage,
    setApplicationError,
    updateServerTime,
    updateRequiredLogin,
    toggleSpinTheWheel,
    togglePromoModal
} = appSlice.actions
